import React from 'react';
import styled from 'styled-components';
import { trackEvent } from '@99designs/tracking';
import { media, colors, spaces } from '../../../../shared/lib/styles';
import Heading from '../../../../shared/components/Heading';
import { FeatureAccordion } from '../../../../shared/components/Accordion';
import { useSite } from '../../../../shared/lib/site';
import Showcase, { DesignerImage, ShowcaseDetail } from './Showcase';
import { getProductDetails, getShowcaseDesigner } from './helpers';
import type { Currency } from '../../../../shared/lib/currency';

const DetailsSectionContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    color: ${colors.white};

    @media ${media('small')} {
        flex-direction: column;
    }
`;

const AccordionContainer = styled.div`
    width: 320px;
    margin-right: ${spaces.space8}px;

    @media ${media('large')} {
        margin-right: 60px;
    }

    @media ${media('normal', 'large')} {
        min-width: 260px;
    }

    @media ${media('medium')} {
        min-width: 270px;
    }

    @media ${media('small')} {
        width: 100%;
        margin-right: 0;
        margin-bottom: ${spaces.space12}px;
    }
`;

interface Props {
    categoryKey: string;
    config: DetailsProps;
    randomFeaturedDesignerNumber: number; // Randomization ties to SSR therefore it's best to be handled outside of the component
}

export interface DetailsProps {
    title: string;
    productDetails: ProductDetail[];
    designerImages: DesignerImage[];
    showcaseDetail: ShowcaseDetail;
}

export interface ProductDetail {
    id: string;
    title: string;
    leadingParagraph?: ParagraphCopy;
    features: string[];
    endingParagraph?: string;
}

interface ParagraphCopy {
    copy: string;
    links?: LinkReplacement[];
}

interface LinkReplacement {
    anchor: string;
    address: string;
    color?: string;
}

const DetailsSection = ({ categoryKey, config, randomFeaturedDesignerNumber }: Props) => {
    const designerImage = getShowcaseDesigner(randomFeaturedDesignerNumber, config.designerImages);
    const site = useSite();
    const currency = site.siteConfig.currency as Currency;

    return (
        <>
            <Heading
                type="h2"
                variations={['h2', 'with-divider']}
                style={{ color: designerImage.featureColor }}
            >
                {config.title}
            </Heading>
            <DetailsSectionContainer>
                <AccordionContainer>
                    <FeatureAccordion
                        data={getProductDetails(config.productDetails, currency)}
                        featureColor={designerImage.featureColor}
                        onItemOpen={({ id, title }) =>
                            trackEvent('Clicked Product Details Accordion on Product Page', {
                                category: categoryKey,
                                id,
                                title,
                            })
                        }
                    />
                </AccordionContainer>

                <Showcase designerImage={designerImage} showcaseDetail={config.showcaseDetail} />
            </DetailsSectionContainer>
        </>
    );
};

export default DetailsSection;
